import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

// util
import { clone, cloneDeep, compact, filter, find, forEach, get, has, isArray, isNull, isObject, isSet, isString, toString, trim, includes } from 'lodash';
import * as luxon from 'luxon';
import { Md5 } from 'ts-md5/dist/md5';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

// models
import { AppState, QUEUE_UPDATE } from '../app-reducer';
import { AppConstants } from '../app.constants';

@Injectable({
  providedIn: 'root'
})

export class ProcessorService {
  applicantAge: any;
  appState$: Observable<any>;
  path: any;
  formValidate = false;
  moveTo: any = new window['MoveTo']({ duration: 300, tolerance: 150 });
  questions: any;
  ipAddress: string;
  baseFormValue: any;
  debug: boolean;
  userRole: string;
  ServedTime: string;
  shownQuestions: any;
  ogFormValue: any;
  counter: number = 0;
  openBlock: boolean = false;
  showQuestionLog = 'showQuestion found this value';
  readyToSign: BehaviorSubject<boolean>;
  readyToSign$ : Observable<boolean>;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private location: Location,
    private appConstants: AppConstants
  ) {
    this.appState$ = store.select('sections');
    this.readyToSign = new BehaviorSubject<boolean>(false);
    this.readyToSign$ = this.readyToSign.asObservable();
  }

  notifyFormChange(status: any) {
    if(status === 'INVALID'){
      this.readyToSign.next(false);
    } else {
      this.readyToSign.next(true);
    }
    
  }

  // is production
  isProduction(): boolean {
    return window['jhConfig'].env === 'production' ? true : false;
  }

  // writes userRole variable used in submitSection
  setUserRole(user: string) {
    this.userRole = user;
  }
  // go to error on submit
  goToErrorLabel() {
    window['setTimeout'](() => {
      const labelTarget = document.querySelector('.invalid-label');
      this.moveTo.move(labelTarget);
    }, 100);
  }

  // go to error on submit
  goToError() {
    window['setTimeout'](() => {
      const target = document.querySelector('.invalid-feedback');
      this.moveTo.move(target);
    }, 100);
  }

  goToElement(element: string) {
    const target = document.querySelector(element);
    this.moveTo.move(target);
  }

  // extract conditional parent QuestionName
  getConditionalParentKey(clause: string) {
    const newClause = trim(clause.split(' ')[0]).replace(/[()]/g, '').replace('AGE_NEAREST', '').replace(/(\.ANY[\S]*)/ig, '');
    return newClause;
  }

  // Equality clause with '==' or '!='
  getConditionalClauseEquality(clause: string) {
    let value = clause ? trim(clause.split(/\S=/)[1]) : null;

    // check if its value has ""; then only return string in between quotes;
    if (/"/g.test(value)) {
      value = value.split(/[""]/)[1];
    } else if (value.includes('NULL')) {
      value = 'null';
    } else if (value.includes('TRUE')) {
      value = 'true';
    } else if (value.includes('FALSE')) {
      value = 'false';
    }
    return value;
  }

  isFormBlank(obj, response = []): boolean {
    for (const key in obj) {
      const val = obj[key];
      const regex1 = /^[$]{0,1}$/;
      const regex2 = /^\d+$/;
      if (isObject(val)) {
        this.isFormBlank(val, response);
      } else if (val == null || regex1.test(val) || key.match(/([Tt]otalAllocation|ConditionName|StateFilter)/) || (regex2.test(key) && val === false)) {
        response.push(true);
      } else {
        response.push(false);
      }
    }
    return !response.includes(false);
  }

  // comparison math for integers clause with '==' or '<=' or '>=' or '<' or '>'
  compareIntegers(clause: string, fieldVal: number, clauseVal: number) {
    if (/==/g.test(clause)) {
      return fieldVal == clauseVal;
    } else if (/<=/g.test(clause)) {
      return fieldVal <= clauseVal;
    } else if (/>=/g.test(clause)) {
      return fieldVal >= clauseVal;
    } else if (/</g.test(clause)) {
      return fieldVal < clauseVal;
    } else if (/>/g.test(clause)) {
      return fieldVal > clauseVal;
    }
  }

  /**
   * Returns logic type from conditional clause
   * @param clause - CondtitionalClause
   * @returns - contains(includes NOT CONTAIN), equality
   */
  checkLogicType(clause) {
    if (/\.ANY/ig.test(clause)) {
      return 'checkArray';
    } else if (/\sin\s[[]/gi.test(clause)) {
      return 'inSet';
    } else if (/AGE_NEAREST/ig.test(clause)) {
      return 'age';
    } else if (/[+]/ig.test(clause)) {
      return 'addition';
    } else if (/contains/ig.test(clause)) {
      return 'contains';
    } else if (/==|!=/.test(clause)) {
      return 'equality';
    } else {
      return null;
    }
  }

  /**
   * Returns boolean: handler for 'CONTAINS' or 'NOT CONTAINS' clause;
   * parent is a multiselect/multichoice
   * values are converted to lowercase for inconsistencies
   * @param clause - conditional clause
   * @param questions - section questions©
   * @param formValue - current applyForm values
   */
  checkContains(clause: string, controlValue, conditionKey, questions) {

    clause = clause.replace(/\(|\)/gi, '');

    let showQuestion = false;
    const parentValue = clause.match(/"(.*?)"/)[1];

    if (isArray(controlValue)) {
      controlValue = toString(this.getCheckboxValues(conditionKey, controlValue, questions));
      if (this.debug) {
        console.log('controlValue CheckboxValues', controlValue);
      }
    } else {
      controlValue = toString(controlValue);
      if (this.debug) {
        console.log('checkContains controlvalue !isArray', controlValue);
      }
    }

    if (controlValue) {
      if (/not contains/ig.test(clause)) {
        showQuestion = !controlValue.includes(parentValue);
      } else {
        showQuestion = controlValue.includes(parentValue);
      }
    } else {
      if (/not contains/ig.test(clause)) {
        showQuestion = true;
      } else {
        showQuestion = false;
      }
    }

    if (this.debug) {
      console.log('checkContains result', showQuestion);
    }

    return showQuestion;
  }

  /**
   * Returns boolean: handler for '==' clause;
   * @param clause - conditional clause
   * @param formValue - current applyForm values
   */
  checkEquality(clause: string, currentBlock, formValue, controlValue, conditionKey, questions) {

    clause = clause.trim();

    let conditionalValue = this.getConditionalClauseEquality(clause);

    // special case for NIGO warnings on SECTIONAProposedInsured that need to pull and compare two values when neither one is the label
    if (typeof conditionalValue === 'string' && conditionalValue.includes('.')) {
      const questionParts = conditionalValue.split('.');
      if (conditionalValue.includes(currentBlock)) {
        conditionalValue = questionParts.length == 2 ? formValue[questionParts[1]] : formValue[questionParts[1]][questionParts[2]];
      } else {
        this.appState$.pipe(first()).subscribe((state: any) => {

          const savedBlocks = Object.keys(state.content);
          if (savedBlocks.includes(questionParts[0])) {
            conditionalValue = questionParts.length == 2 ? state.content[questionParts[0]][questionParts[1]] : state.content[questionParts[0]][questionParts[1]][questionParts[2]];
          }
        });
      }
    }

    if (isArray(controlValue)) {
      controlValue = toString(this.getCheckboxValues(conditionKey, controlValue, questions));
      // if (this.debug) {
      //   console.log('controlValue (for checkboxes)', controlValue);
      // }
    } else {
      controlValue = toString(controlValue);
    }

    controlValue = controlValue ? (controlValue === '$' ? 'null' : (controlValue === '$0' ? '0' : controlValue)) : 'null';
    let result: boolean;

    if (/!=/.test(clause)) {
      result = controlValue !== conditionalValue;
      // if (this.debug) {
      //   console.log('checkEquality equation', controlValue, '!==', conditionalValue);
      // }
    } else {
      result = controlValue === conditionalValue;
      // if (this.debug) {
      //   console.log('checkEquality equation', controlValue, '===', conditionalValue);
      // }
    }

    // if (this.debug) {
    //   console.log('checkEquality result', result);
    // }

    return result;

  }

  /**
   * Returns boolean: handler for age calculated clause;
   * @param clause - conditional clause
   * @param formValue - current applyForm values
   */
  checkInSet(clause: string, controlValue) {
    const setString = trim(clause.split(/\sIN\s/ig)[1]).replace(/[\["\]]/g, '').replace(/\s/g, '').replace(/\)/g, '');
    const set = setString.split(",");
    const arrayEquality = (element) => element ? (element === controlValue) : false;

    const result = set.some(arrayEquality);

    if (this.debug) {
      console.log('checkInSet response', result);
    }

    return result;
  }

  /**
   * Returns boolean: handler for age calculated clause;
   * @param clause - conditional clause
   */
  checkArray(clause: string, controlValue) {
    const subClause = trim(clause.split('.ANY')[1]).replace(/[{()}]/g, '');
    let logicType = 'single';
    let result = false;

    if (this.debug) {
      console.log('checkArray subClause', subClause);
    }

    // OR logic
    if (/\\"|"(?:\\"|[^"])"|(\ or )/ig.test(subClause) && !/\\"|"(?:\\"|[^"])"|(\ and )/i.test(subClause)) {
      logicType = 'or';
      // AND logic
    } else if (/\\"|"(?:\\"|[^"])"|(\ and )/ig.test(subClause) && !/\\"|"(?:\\"|[^"])"|(\ or )/ig.test(subClause)) {
      logicType = 'and';
    }

    const conditional = logicType === 'single' ? [subClause] : logicType === 'or' ? subClause.split(/ or /ig) : subClause.split(/ and /ig);
    const conditionalValues = [];

    controlValue.forEach((responseSet, i) => {

      if (conditional.length === 1) {
        const arr = this.checkArrayBaseCalculation(clause, subClause, responseSet);
        conditionalValues[i] = arr.includes(false) ? false : true;
      } else {
        const subCV = [];
        conditional.forEach((cond, j) => {
          const arr = this.checkArrayBaseCalculation(clause, cond, responseSet);
          subCV[j] = arr.includes(false) ? false : true;
        });
        conditionalValues[i] = subCV;
      }
    });

    if (this.debug) {
      console.log('checkArray conditionalValues:', conditionalValues);
    }

    if (logicType === 'single') {
      result = conditionalValues.includes(true);
    } else if (logicType === 'or') {
      conditionalValues.forEach(x => {
        if (x.includes(true)) {
          result = true;
        }
      });
    } else if (logicType === 'and') {
      conditionalValues.forEach(x => {
        if (!x.includes(false)) {
          result = true;
        }
      });
    }

    if (this.debug) {
      console.log('checkArray result', result);
    }

    return result;

  }


  checkArrayBaseCalculation(clause: string, subClause: string, controlValue) {

    const result = [];

    const subClauseQ = this.getConditionalParentKey(subClause);

    if (this.debug) {
      console.log('checkArrayBaseCalc subClause', subClause);
      console.log('checkArrayBaseCalc subClauseQ', subClauseQ);
      console.log('checkArrayBaseCalc controlValue', controlValue);
    }

    if (/==|!=/.test(subClause)) {

      const subClauseConditionalValue = this.getConditionalClauseEquality(subClause);

      const negator = /!=/.test(subClause) ? true : false;

      if (controlValue[subClauseQ] && controlValue[subClauseQ].toString() === subClauseConditionalValue.toString()) {
        result.push(negator ? false : true);
      } else {
        result.push(negator ? true : false);
      }

      if (this.debug) {
        console.log('checkArrayBaseCalc equation clauseVal', subClauseConditionalValue);
        console.log('checkArrayBaseCalc equation response', result);
      }
    } else if (/\sin\s[[]/gi.test(subClause)) {

      let subClauseKey = trim(subClause.split(/\sIN\s/ig)[0]);
      subClauseKey = subClauseKey.includes('!(') ? subClauseKey.substring(2) : subClauseKey.includes('!') ? subClauseKey.substring(1) : subClauseKey;
      // const negator = /!/.test(subClause) ? true : false;
      const setString = trim(clause.split(/\sIN\s/ig)[1]).replace(/[\["\()\{}\]]/g, '').replace(/\s/g, '');
      const set = setString.split(',');

      if (this.debug) {
        console.log(set, 'contains', controlValue[subClauseKey], set.includes(controlValue[subClauseKey]));
      }
      if (set.includes(controlValue[subClauseKey])) {
        result.push(true);
      } else {
        result.push(false);
      }

      if (this.debug) {
        console.log('checkArrayBaseCalc inSet subClauseKey', subClauseKey);
        console.log('checkArrayBaseCalc inSet set', set);
        console.log('checkArrayBaseCalc inSet result', result);
      }
    } else {
      console.log('checkArray needs more logic for this');
    }
    return result;
  }

  /**
   * Returns boolean: handler for age NEAREST calculated clause;
   * @param clause - conditional clause
   * @param formValue - current applyForm values
   */
  checkAge(clause: string, controlValue) {
    const targetAge = parseInt(this.getConditionalClauseEquality(clause));
    let ageEligible = false;

    this.appState$.pipe(first()).subscribe((state: any) => {

      if (isSet(state.PolicyCreateDate)) {
        const end = luxon.DateTime.fromFormat(state.PolicyCreateDate, this.appConstants.monthYearFormat);
        this.calculateAgeNearest(controlValue, end);
      } else {
        this.calculateAgeNearest(controlValue, luxon.DateTime.now());
      }
      if (controlValue) {
        ageEligible = this.compareIntegers(clause, this.applicantAge, targetAge);
      } else {
        return false;
      }
    });

    if (this.debug) {
      console.log('checkAge', this.applicantAge, '; result', ageEligible);
    }

    return ageEligible;
  }
  calculateAgeNearest(controlValue: any, end: any) {
    let nextBirthdayDate;
    const start = luxon.DateTime.fromFormat(controlValue, this.appConstants.monthYearFormat);
    //determine birthday has passed this year or not
    const currYearBirthday = start.set({ year: luxon.DateTime.now().year });
    if (currYearBirthday <= end) {//true= birthday has passed, false = birthday has not passed
      //add a year to the birthday to find next upcoming birthday
      nextBirthdayDate = currYearBirthday.plus({ years: 1 });
    } else {
      nextBirthdayDate = currYearBirthday;
    }
    if (nextBirthdayDate.diff(end, 'months').months > 6) {
      //more than 6 months away from their bday, round down
      this.applicantAge = Math.floor(end.diff(start, 'years').years);
    } else {
      //less than or equal to 6 months away from their bday, round up
      this.applicantAge = Math.floor(end.diff(start, 'years').plus({ years: 1 }).years);
    }
  }

  /**
   * Returns boolean: handler for clauses that rely on added values;
   * @param clause - conditional clause
   * @param formValue - current applyForm values
   */
  checkSumValues(clause: string, currentBlock: any, formValue: any) {

    const targetVal = parseInt(trim(clause.split(' ')[clause.split(' ').length - 1]));
    let mathEligible = false;

    const sumParts = [];
    this.appState$.pipe(first()).subscribe((state: any) => {
      let addedFields = clause.match(/^(.*?)\)/);
      if (addedFields) {
        addedFields = addedFields[1].split(' + ');
        addedFields.forEach(val => {
          let fieldValues;
          if (val.includes(currentBlock)) {
            val = val.replace(currentBlock + '.', '');
            fieldValues = formValue[val] ? formValue[val] : 0;
          } else {
            fieldValues = get(state, 'content.' + val);
          }
          sumParts.push(Number(fieldValues ? fieldValues.toString().replace(/\$|,/g, '') : 0));
        });
      } else {
        return false;
      }
    });

    const sumVal = sumParts.reduce((a, b) => a + b, 0);

    if (sumVal) {
      mathEligible = this.compareIntegers(clause, sumVal, targetVal);
    } else {
      return false;
    };

    if (this.debug) {
      console.log('checkSumValues sum =', sumVal, ' from', sumParts);
      console.log('checkSumValues result', mathEligible);
    }

    return mathEligible;
  }

  showConditionalOption(clause: any, formValue, baseFormValue?) {

    return this.analyzeConditionalArray(clause, this.questions, formValue, baseFormValue);
  }

  isNavBlockVisible(clause: any, formValue: any, questions: any = null) {

    // sets var used to trigger additional logic in showQuestion to prevent blocks appearing prematurely
    this.openBlock = true;

    const result = this.analyzeConditionalArray(clause, questions, formValue, null);

    // unsets var used in showQuestion
    this.openBlock = false;
    return result;
  }

  analyzeConditionalArray(clause: any, questions, formValue, baseFormValue?) {

    let negator = false;
    let subNegator = false;
    let allNegator = false;

    // TODO: determine if this can be removed or if it's needed to catch hidden variations and conditions
    if (!clause || clause === '0' || clause === '0.0') {
      return false;
    }

    if (typeof clause === 'string') {
      console.log(clause);
      console.log('^^^ string clause ^^^');
    }

    // TODO: Do we still need this? - yes, for now
    // create questions var for use when not passed in when not present coming from showConditionalOption()
    this.questions = questions ? questions : this.questions;

    let out = cloneDeep(clause);

    if (out[0] === '!') {
      if (this.debug) {
        console.log('Found Array Negator');
      }
      allNegator = true;
      out = out.slice(1);
    }

    for (let i = 0; i < out.length; i++) {
      // create array of subclause results
      if (out[i] && out[i] != 'AND' && out[i] != 'OR') {
        if (isArray(out[i])) {
          out[i] = compact(out[i]);
          if (typeof out[i][0] === 'string' && out[i][0].match(/^\!\(/g)) {
            if (this.debug) {
              console.log('Negating this:', cloneDeep(out[i]));
            }
            // negator = true;
            out[i][0] = out[i][0].replace(/^\!\(/g, '');
            out[i].unshift('!');
          }
          for (let j = 0; j < out[i].length; j++) {

            if (isArray(out[i][j])) {
              out[i][j] = compact(out[i][j]);
              if (typeof out[i][j][0] === 'string' && out[i][j][0].match(/^\!\(/g)) {
                if (this.debug) {
                  console.log('Also negating this:', out[i][j]);
                }
                out[i[j]].unshift('!');
                // subNegator = true;
                out[i][j][0] = out[i][j][0].replace(/^\!\(/g, '');
              }
              if (this.debug) {
                console.log('what is here now?', cloneDeep(out[i][j]));
              }
              for (let k = 0; k < out[i][j].length; k++) {
                out[i][j][k] = (out[i][j][k] != 'AND' && out[i][j][k] != 'OR' && out[i][j][k] != '!') ? this.crunchConditionalClause(out[i][j][k], formValue, baseFormValue) : out[i][j][k];
              }
            } else {
              out[i][j] = (out[i][j] != 'AND' && out[i][j] != 'OR' && out[i][j] != '!') ? this.crunchConditionalClause(out[i][j], formValue, baseFormValue) : out[i][j];
            }
          }
        } else {
          out[i] = this.crunchConditionalClause(out[i], formValue, baseFormValue);
        }
      }
    };
    if (this.debug) {
      console.log('ConditionalClause results array', cloneDeep(out));
    }

    let show = false;

    for (let i = out.length - 1; i >= 0; i--) {
      let subBool;
      let subSubBool;
      if (isArray(out[i])) {
        for (let j = out[i].length - 1; j >= 0; j--) {
          if (isArray(out[i][j])) {
            if (out[i][j][0] === '!') {
              subNegator = true;
              out[i][j].shift();
            }
            if (out[i][j].includes('AND')) {
              subSubBool = out[i][j].includes(false) ? false : true;
            } else if (out[i][j].includes('OR')) {
              subSubBool = out[i][j].includes(true) ? true : false;
            }
            out[i][j] = subNegator ? !subSubBool : subSubBool;
          }
          subNegator = false;
        }
        if (out[i][0] === '!') {
          negator = true;
          out[i].shift();
        }
        if (out[i].includes('AND')) {
          subBool = out[i].includes(false) ? false : true;
        } else if (out[i].includes('OR')) {
          subBool = out[i].includes(true) ? true : false;
        }
        out[i] = negator ? !subBool : subBool;
      }
      negator = false;
    };

    if (out.length == 1) {
      show = out[0];
    } else if (out.includes('AND') && out.includes('OR')) {
      show = this.resolveAndOr(out);
    } else if (out.includes('AND')) {
      show = out.includes(false) ? false : true;
    } else if (out.includes('OR')) {
      show = out.includes(true) ? true : false;
    }

    if (allNegator) {
      show = !show;
    }

    if (this.debug) {
      console.log((allNegator ? 'Array negated, so ConditionalClause array result is:' : 'ConditionalClause array result is:'), show);
    }
    return show;

  }

  resolveAndOr(clause) {
    // console.log('clause in', clone(clause));
    if (clause.includes('AND')) {
      const position = clause.indexOf('AND');
      const andPart = clause.slice(position - 1, position + 2).includes(false) ? false : true;
      clause.splice(position - 1, 3, andPart);
      return this.resolveAndOr(clause);
    } else if (clause.includes('OR')) {
      // console.log('only ORs, so', clause.includes(true) ? true : false);
      return clause.includes(true) ? true : false;
    }
  }

  crunchConditionalClause(clause: any, formValue, baseFormValue) {

    // TODO: determine if this can be removed or if it's needed to catch hidden variations and conditions
    if (clause === '0') {
      // console.log('find this clause', clause);
      return false;
    }

    let showQuestion = false;

    if (typeof clause === 'string' && clause === '!') {
      return;
    }

    // if (typeof clause === 'object') {
    //   return this.clauseArray(clause, formValue);
    // }

    const negator = clause.includes('!(') ? true : false;
    if (/AGE_NEAREST\(/ig.test(clause) && /(\)\s\>)|(\)\s\=)|(\)\s\<)/ig.test(clause)) {
      clause = clause.replace(/\!\(/g, '(');
    } else {
      clause = clause.replace(/\!\(|\(/g, '');
    }
    if (this.debug) {
      console.log(negator ? 'clause negated' : 'clause NOT negated');
    }

    // Remove containers that can contain and/or for parsing in the folloing if
    const simpleClause = clause.replace(/([\{][^\}]*[^\{]*[\}])/ig, '');

    /**
     * AND & OR logic
     */
    // if (/\\"|"(?:\\"|[^"])"|(\ or )/ig.test(clause) && /\\"|"(?:\\"|[^"])"|(\ and )/i.test(clause)) {    [^\)]\s(and)\s[^\(]
    if (/\bor\b(?![\w\s].*[\}])/ig.test(simpleClause) && /\band\b(?![\w\s].*[\}])/ig.test(simpleClause)) {

      if (this.debug) {
        console.log('AND & OR logic clause:', clause);
      }
      const conditionalOrSet = clause.split(/(?![\{][\s\S]*)and(?![\s\S]*[\}])/ig);
      const conditionalOrValues = {};

      if (this.debug) {
        console.log('conditionalOrSet', conditionalOrSet);
      }

      // split OR's
      conditionalOrSet.forEach((cond, index) => {
        // sanitize: remove parens except where needed
        if (!/AGE_NEAREST\(/ig.test(cond) && !/(\)\s\>)|(\)\s\=)|(\)\s\<)/ig.test(cond)) {
          cond = cond.replace(/\(|\)/g, '');
        }

        // split at OR
        const conditional = cond.split(/ or /ig);

        // push to object group
        conditional.forEach(orCond => {
          showQuestion = this.showQuestion(orCond.trim(), this.questions, formValue, baseFormValue);
          if (this.debug) {
            console.log('orCond:', orCond.trim(), 'is', showQuestion);
          }

          // initialize and push to conditionalOrValues
          (conditionalOrValues[index] || (conditionalOrValues[index] = [])).push(showQuestion);
        });
      });

      // collect all sets and check if all are true
      const conditionalAndValues = [];
      forEach(conditionalOrValues, boolValues => {
        conditionalAndValues.push(includes(boolValues, true) ? true : false);
      });
      if (this.debug) {
        console.log('AND/OR result:', conditionalAndValues.includes(false) ? false : true);
      }

      // AND logic: check if there are no false values
      showQuestion = conditionalAndValues.includes(false) ? false : true;

    } else if (/\bor\b(?![\w\s].*[\}])/ig.test(simpleClause) && !/\band\b(?![\w\s].*[\}])/ig.test(simpleClause)) {
      // OR logic

      if (this.debug) {
        console.log('OR logic clause:', clause);
      }
      const conditional = clause.split(/ or /ig);

      // loop & exit if single true clause found;
      showQuestion = conditional.some(cond => {
        showQuestion = this.showQuestion(cond.trim(), this.questions, formValue, baseFormValue);
        if (this.debug) {
          console.log('cond:', cond.trim(), 'is', showQuestion);
        }

        return showQuestion === true;
      });

    } else if (/\band\b(?![\w\s].*[\}])/ig.test(simpleClause) && !/\bor\b(?![\w\s].*[\}])/ig.test(simpleClause)) {
      // AND logic

      if (this.debug) {
        console.log('AND logic clause:', clause);
      }
      const conditional = clause.split(/ and /ig);
      const conditionalValues = [];

      conditional.forEach(cond => {
        cond = trim(cond);
        // sanitize: remove parens except where needed
        if (!/AGE_NEAREST\(/ig.test(cond) && !/(\)\s\>)|(\)\s\=)|(\)\s\<)/ig.test(cond)) {
          cond = cond.replace(/\(|\)/g, '');
        }
        showQuestion = this.showQuestion(cond, this.questions, formValue, baseFormValue);
        if (this.debug) {
          console.log('cond:', cond, 'is', showQuestion);
        }

        conditionalValues.push(showQuestion);
      });

      if (this.debug) {
        console.log('conditionalValues', conditionalValues);
      }

      // find if there is a false value;
      showQuestion = conditionalValues.includes(false) ? false : true;

    } else {
      // SINGLE CLAUSE

      if (this.debug) {
        console.log('single clause', clause);
      }
      showQuestion = this.showQuestion(clause, this.questions, formValue, baseFormValue);
    }

    if (this.debug) {
      console.log('question negated?', negator, '. So result is', negator ? !showQuestion : showQuestion);
    }
    return negator ? !showQuestion : showQuestion;
  }

  enableComments(debug) {
    this.debug = debug;
  }

  /**
   * Determines visibility of question - CONDITIONAL CLAUSE logic - returns bool;
   * @param clause - conditional clause
   * @param questions - section questions
   * @param formValue  - current applyForm values
   */
  showConditionalQuestion(question: any, questions, formValue, baseFormValue?) {

    if (this.debug) {
      console.log('analysing this question:', question.QuestionName);
      console.log('with this conditional clause:', question.ConditionalClause);
    }

    // this.counter += 1;
    // console.log('showConditionalQuestion', this.counter);

    if (!question.ConditionalClause) {
      return true
    } else {
      return this.analyzeConditionalArray(question.ConditionalClause, questions, formValue, baseFormValue);
    }
  }

  showQuestion(cond, questions, formValue, baseFormValue) {

    let showQuestion = false;

    const conditionKey = this.getConditionalParentKey(cond);
    if (baseFormValue) {
      // get form array question names
      const baseGroupQuestionNames = Object.keys(baseFormValue);
      // switch to baseFormValue if it contains the conditionKey
      formValue = baseGroupQuestionNames.includes(conditionKey) ? baseFormValue : formValue;
    }
    let conditionBlock = conditionKey.includes('.') ? trim(conditionKey.split(/\./)[0]) : null;
    conditionBlock = find(this.router.config, { data: { blockName: conditionBlock } }) ? conditionBlock : null;
    let currentBlock = find(this.router.config, { path: location.pathname.substring(1) });
    currentBlock = currentBlock.data.blockName;
    let conditionVal;

    // Getting value from form when conditionBlock matches the current page
    if (conditionBlock == currentBlock) {
      const conditionQName = (conditionKey.substring(conditionBlock.length + 1)).split(/\.(?!ANY)/gi);
      conditionVal = formValue ? (conditionQName.length == 1 ? formValue[conditionQName[0]] : formValue[conditionQName[0]][conditionQName[1]]) : false;

      if (this.debug) {
        console.log(this.showQuestionLog, conditionVal);
        console.log('↳ read from the formValue', formValue);
        console.log(' ↳ because conditionBlock is current page', conditionBlock);
      }
      if (conditionVal == undefined) {
        if (JSON.stringify(formValue).indexOf(conditionQName[conditionQName.length - 1]) > -1) {
          const questionName = conditionQName[conditionQName.length - 1];
          const nestedValue = function (formObj: any) {
            forEach(formObj, (val, key) => {
              if (key === questionName) {
                conditionVal = val;
                return false;
              } else if (isObject(val)) {
                return nestedValue(val);
              }
            });
          }
          nestedValue(formValue);
        }
        if (this.debug) {
          console.log('  ↳ didn\'t find a value, so searching whole form structure for a question name match');
          if (conditionVal) {
            console.log('   ↳ found it!', conditionVal);
          } else {
            console.log('   ↳ didn\'t find it :(');
          }
        }
      }
    } else if (conditionBlock) {
      // Getting value from state when conditionKey contains a block that is not currently open
      this.appState$.pipe(first()).subscribe((state: any) => {
        conditionVal = get(state, 'content.' + conditionKey) ? get(state, 'content.' + conditionKey) : null;
      });

      if (this.debug) {
        console.log(this.showQuestionLog, conditionVal);
        console.log('read from state because conditionBlock is', conditionBlock);
      }
    } else {
      conditionVal = formValue[conditionKey];

      if (this.debug) {
        console.log(this.showQuestionLog, conditionVal);
        console.log('read from form because conditionBlock is', conditionBlock);
      }
    }

    if (this.openBlock) {
      if (!cond.includes('+') && !cond.includes('PartnerID') && (conditionVal === null || (isArray(conditionVal) && !conditionVal.some(x => x === true) && (typeof conditionVal[0] !== 'object')))) {
        if (this.debug) {
          // console.log('conditionVal: ', conditionVal);
          // console.log('isArray(conditionVal): ', isArray(conditionVal));
          // console.log('!conditionVal.some(x => x === true): ', conditionVal ? !conditionVal.some(x => x === true) : 'blah');
          // console.log('isArray(conditionVal[0]): ', conditionVal && isArray(conditionVal) ? conditionVal[0] : 'no');
          console.log('deferring');
        }
        // deferring analysis of block conditionality in left nav because it is based on a question that has no response
        return false;
      }
    }

    const clauseLogicType = this.checkLogicType(cond);

    if (!conditionVal && clauseLogicType !== 'addition' && clauseLogicType !== 'equality') {
      return false;
    }

    if (this.debug) {
      console.log('showQuestion clauseLogicType', clauseLogicType);
    }

    if (clauseLogicType === 'checkArray') {
      showQuestion = this.checkArray(cond, conditionVal);
    } else if (clauseLogicType === 'inSet') {
      showQuestion = this.checkInSet(cond, conditionVal);
    } else if (clauseLogicType === 'age') {
      showQuestion = this.checkAge(cond, conditionVal);
    } else if (clauseLogicType === 'addition') {
      showQuestion = this.checkSumValues(cond, currentBlock, formValue);
    } else if (clauseLogicType === 'contains') {
      showQuestion = this.checkContains(cond, conditionVal, conditionKey, questions);
    } else if (clauseLogicType === 'equality') {
      showQuestion = this.checkEquality(cond, currentBlock, formValue, conditionVal, conditionKey, questions);
    }
    return showQuestion;
  }

  /**
   * convert checkbox values array to corresponding names
   * returns array of string (option titles/names)
   * @param questions - section questions
   * @param questionName - QuestionName
   * @param boolValues - form array values
   * @param isLowerCase - (bool) used for matching/comparison
   */
  getCheckboxValues(questionName: string, boolValues, questions: any) {

    if (!boolValues || !boolValues.includes(true)) { return []; }
    let parentOptions = null;

    // get options from state for questions on an earlier block
    if (questionName.includes('.')) {
      this.appState$.pipe(first()).subscribe((state: any) => {
        const block = find(state.questions, { Name: trim(questionName.split('.')[0]) });
        parentOptions = find(block.Questions, { QuestionName: trim(questionName.split('.')[1]) });
      });
    } else {
      // get options from current block
      parentOptions = find(questions, { QuestionName: questionName });
    }

    if (parentOptions) {
      const combinedArray = boolValues.map((val: boolean, i: number) => {
        return {
          isSelected: val,
          ...parentOptions['QuestionOptions'][i]
        };
      });

      // use 'value' from custom options (optionMap) ||  use 'Name' from QuestionOptions
      return combinedArray.filter(item => item.isSelected).map(obj => {
        return obj.Name ? obj.Name : obj.value;
      });

    }

  }

  setServedTime() {
    this.ServedTime = this.getTimeStamp();
  }

  getTimeStamp() {
    Date.prototype.toISOString = function () {
      const tzo = -this.getTimezoneOffset(),
        dif = tzo >= 0 ? '+' : '-',
        pad = function (num) {
          const norm = Math.floor(Math.abs(num));
          return (norm < 10 ? '0' : '') + norm;
        };
      return this.getFullYear() +
        '-' + pad(this.getMonth() + 1) +
        '-' + pad(this.getDate()) +
        'T' + pad(this.getHours()) +
        ':' + pad(this.getMinutes()) +
        ':' + pad(this.getSeconds()) +
        dif + pad(tzo / 60) +
        ':' + pad(tzo % 60);
    }
    const dt = new Date();
    return dt.toISOString();
  }

  // Method to trim all leading and trailing spaces from field values
  trimAll(obj) {
    Object.keys(obj).map(k => {
      obj[k] = obj[k] == null ? null : typeof obj[k] == 'string' ? obj[k].trim() : typeof obj[k] == 'object' ? this.trimAll(obj[k]) : obj[k];
    });
    return obj;
  }

  // erases shownQuestions array and sets ogFormValue from navigation service, for use in transformPayload
  eraseShownQuestions(ogFormValue) {
    this.ogFormValue = ogFormValue;
    this.shownQuestions = [];
  }

  /**
  * Returns object for payload submission
  * Transforms formvalues to payload schema
  * @param formValue - current applyForm values
  * @param questions - section questions
  */
  transformPayload(formValue: any, questions: any, topLevel: boolean = true) {
    const formKeys = Object.keys(formValue);
    for (let i = formKeys.length - 1; i >= 0; i--) {
      const key = formKeys[i];
      const value = formValue[key];
      const question = find(questions, { QuestionName: key });
      if (typeof question === 'undefined') {
        delete formValue[key];
      } else if (typeof question === 'undefined' && !isObject(formValue[key])) {
        // console.log(key, '=', value);
        // console.log('formValue[key]', formValue[key]);
        // console.log('questions:', questions);
        delete formValue[key];
      } else if (isArray(value) && question.MultipleResponse) {
        let isEmpty;
        forEach(value, q => {
          isEmpty = !Object.values(q).some(x => x !== null);
        });
        if (isEmpty) {
          formValue[key] = null;
        } else {
          if (key === 'IULProceedsAlloc') {
            forEach(value, (item, index) => {
              forEach(item['IULProceedsAllocToCompound'], (y, x) => {
                formValue['IULProceedsAlloc'][index][x] = y;
              });
              delete formValue['IULProceedsAlloc'][index]['IULProceedsAllocToCompound'];
              delete formValue['IULProceedsAlloc'][index]['PercentTotal'];
            });
          }
          value.forEach(arr => {
            this.transformPayload(arr, questions, false);
          });
        }
      } else if (isObject(value) && question.QuestionType === 'Compound') {
        if (key === 'IULPremPaymentAllocation' || key === 'IULFixedAcctAlloc') {
          forEach(formValue[key], (y, x) => {
            if (y == null) {
              formValue[x] = null;
            } else if (typeof y === "number") {
              formValue[x] = y;
            } else {
              formValue[x] = Number(y.replace(/[\D]/g, ''));
            }
          });
          if (key === 'IULPremPaymentAllocation') {
            delete formValue['IULReminder'];
            delete formValue['PercentTotal'];
          } else {
            delete formValue['DollarTotal'];
          }
          delete formValue[key];
        }
        this.transformPayload(value, questions, false);
      } else if (!isObject(value) && question.QuestionType === 'Compound') {
        delete formValue[key];
      } else {
        formValue[key] = this.reformatQuestion(value, question, formValue, questions);
        this.shownQuestions.push(key);
      }
    }

    if (topLevel) {
      // Adds certain conditionally hidden questions back to payload to submit null values
      // for overwriting previously submitted and then conditionally removed questions
      questions.forEach(q => !this.shownQuestions.includes(q.QuestionName) && q.QuestionType !== 'Compound' && q.QuestionType !== 'MultiSelect' && !q.MultipleResponseChild ? formValue[q.QuestionName] = null : null);
    }

    // fix for rawValue passing nulls for all questions in suppressed multipleresponse arrays
    forEach(formValue, (val, index) => {
      if (Array.isArray(val) && val.length === 1) {
        formValue[index] = !Object.values(val[0]).every(x => typeof x == 'object' && x == null) ? val : null;
      };
    });

    return this.trimAll(formValue);
  }

  // Detects CA-specific medical conditions that need to be mapped to ConditionDiagnosisOtherDetails and alters the payload
  handleCaliforniaMedical(payload: any, questions: any) {
    if ('MedicalConditionDetails' in payload) {
      payload['MedicalConditionDetails'].forEach(arrayChild => {
        const formKeys = Object.keys(arrayChild);
        for (let i = formKeys.length - 1; i >= 0; i--) {
          const key = formKeys[i];
          const value = arrayChild[key];
          const question = find(questions, { QuestionName: key });
          if (question.QuestionType === 'MultiChoice' && value) {
            const selectedOption = question.QuestionOptions.find(x => x.key === value);
            if (selectedOption.MapToOther) {
              arrayChild[key] = 'Other';
              arrayChild['ConditionDiagnosisOtherDetails'] = value;
            }
          }
        }
      });
    }
    return payload;
  }

  reFormatTINResponse(payload: any) {
    if ('PolicyOwnerInformation' in payload) {
      if (payload['PolicyOwnerInformation']) {
        payload['PolicyOwnerInformation'].forEach(arrayChild => {
          arrayChild['PolicyOwnerTaxId'] = arrayChild['PolicyOwnerTaxId'] ? arrayChild['PolicyOwnerTaxId'].replace(/[^0-9]/g, '') : arrayChild['PolicyOwnerTaxId'];
          if (arrayChild['PolicyOwnerType'] == 'ExistingTrust' || arrayChild['PolicyOwnerType'] == 'TrustToBeEstablished') {
            if (arrayChild['SigningOfficerCompound']) {
              arrayChild['SigningOfficerCompound'].forEach(signatorInfo => {
                signatorInfo['TrusteeEntityTaxID'] = signatorInfo['TrusteeEntityTaxID'] ? signatorInfo['TrusteeEntityTaxID'].replace(/[^0-9]/g, '') : signatorInfo['TrusteeEntityTaxID'];
              })
            }
          }
        });
      }
    }
    return payload;
  }

  reformatQuestion(value: any, question: any, formValue: any, questions: any) {
    const { QuestionName, QuestionType, MultipleResponse } = question;

    if (value) {

      if (QuestionType === 'Address') {
        if (!value.Street && !value.City && !value.State) {
          return null;
        } else {
          const address = {
            Street: value.Street,
            Street2: value.Street2 ? value.Street2 : null,
            City: value.City,
            State: value.State,
            Zip: value.Zip
          };
          return address;
        }

      } else if (QuestionType === 'BloodPressure') {
        if (!value.Systolic && !value.Diastolic) {
          return null;
        } else {
          const bloodPressure = {
            Systolic: Number(value.Systolic),
            Diastolic: Number(value.Diastolic)
          };
          return bloodPressure;
        }

      } else if (QuestionType === 'Date') {
        const date = luxon.DateTime.fromFormat(value, this.appConstants.monthYearFormat).toFormat('yyyy-MM-dd');
        // console.log(' date: ' + date.toString());
        if (date === 'Invalid date') {
          return null;
        }
        return date;

      } else if (QuestionType === 'Decimal') {
        return Number(value.replace(/[^0-9\.-]+/g, ''));

      } else if (QuestionType === 'File') {
        if (value.Response === '{\"FileName\":\"none\"}') {
          return null;
        } else {
          return JSON.parse(value.Response);
        }

      } else if (QuestionType === 'Integer') {
        return Number(value.replace(/[^0-9\.-]+/g, ''));

      } else if (QuestionType === 'Length') {
        if (!value.Feet && !value.Inches) {
          return null;
        } else {
          const length = {
            Feet: Number(value.Feet),
            Inches: Number(value.Inches),
          };
          return length;
        }

      } else if (QuestionType === 'MonthYear') {
        const monthyear = {
          Month: Number(value.split('/')[0]),
          Year: Number(value.split('/')[1]),
        };
        if (monthyear.Month == 0 || isNaN(monthyear.Year)) {
          return null;
        }
        return monthyear;

      } else if (QuestionType === 'MultiSelect') {
        return value.includes(true) ? this.getCheckboxValues(QuestionName, value, questions) : null;

      } else if (QuestionType === 'Name') {
        if (!value.FirstName && !value.LastName) {
          return null;
        } else {
          const name = {
            Title: value.Title ? value.Title.replace(/[^a-z0-9]/ig, '') : null,
            FirstName: value.FirstName,
            MiddleName: value.MiddleName ? value.MiddleName : null,
            LastName: value.LastName,
            Suffix: value.Suffix ? value.Suffix.replace(/[^a-z0-9]/ig, '') : null
          };
          return name;
        }

      } else if (QuestionType === 'Phone') {
        return value.replace(/-/g, '');

      } else if (QuestionType === 'Physician') {
        if (!value.Name && !value.Phone) {
          return null;
        } else {
          const physician = {
            Name: value.Name,
            Phone: value.Phone,
            Address: {
              'Street': value.Street,
              'Street2': value.Street2 ? value.Street2 : null,
              'City': value.City,
              'State': value.State,
              'Zip': value.Zip
            }
          };
          return physician;
        }

      } else if (QuestionType === 'SSN') {
        return value.replace(/-/g, '');

      } else if (QuestionType === 'Signature') {
        const signature = {
          FullName: value,
          DateX: this.ogFormValue['DateX'] ? this.ogFormValue['DateX'] : luxon.DateTime.now().toISO().toString(),
          IPAddress: this.ogFormValue['customerIpAddress'] ? this.ogFormValue['customerIpAddress'] : null
        };
        return signature;

      } else if (QuestionType === 'YesNo') {
        return value === 'true' ? true : false;

      } else if (QuestionType === 'File') {
        return JSON.parse(value.Response);

      } else {
        return value;
      }
    } else {
      return null;
    }
  }

  // Submit section
  submitSection({ appAuthId = null, section = null, payload = null } = {}, changedStatus = false) {

    // Cancel submission if identical block previously submitted
    // Excepting DeclarationsInsured and TransmittalBlock because they are needed to trigger merge documents in JHADE
    if (this.diffResubmittedPayload(payload, section) && !changedStatus) {
      return;
    }

    // generate new payload
    let payloadAnswers = [];
    forEach(payload, (value: any, key: any) => {
      // make strings
      if (!isNull(value)) {
        value = window['JSON'].stringify(value);
      }

      const update = {
        'QuestionName': key,
        'AnswerText': isString(value) && !trim(value) ? null : value
      };

      payloadAnswers = [...payloadAnswers, update];
    });

    // section payload for submission
    const sectionPayload = {
      Role: this.userRole,
      Validation: 'enabled',
      AppAuthId: appAuthId,
      EAppRawPayload: this.generateRawPayload(),
      QuestionBlock: section,
      QuestionResponses: payloadAnswers,
      ServedTime: this.ServedTime,
      SubmissionTime: this.getTimeStamp()
    };

    // console.log('payload', sectionPayload);

    // update payload queue
    this.store.dispatch({
      type: QUEUE_UPDATE,
      payload: sectionPayload
    });
  }

  // Compares payload hash with previously submitted hash in eAppRawPayload
  // submits to JHADE only if they don't match or none previous.
  diffResubmittedPayload(payload, section): boolean {
    const md5 = new Md5();
    const hash = md5.appendStr(JSON.stringify(payload)).end();
    let hashSame: boolean;

    this.appState$.pipe(first()).subscribe((state: any) => {
      const oldHash = state.content['payLoadHash'] ? state.content['payLoadHash'][section] : undefined;
      if (typeof oldHash != "undefined") {
        if (hash === oldHash) {
          hashSame = true;
        } else {
          state.content['payLoadHash'] = { ...state.content['payLoadHash'], [section]: hash };
          hashSame = false;
        }
      } else {
        state.content['payLoadHash'] = { ...state.content['payLoadHash'], [section]: hash };
        hashSame = false;
      }
    });
    return hashSame;
  }

  // utility: generate raw payload for JHADE
  generateRawPayload() {
    let content = {};

    this.appState$.pipe(first()).subscribe((state: any) => {
      content = { ...state.content, agentData: state.agentData };
    });

    return window['btoa'](unescape(encodeURIComponent(window['JSON'].stringify(content))));
  }

  // Skip page validation if welcome page toggle set
  noValidation(bool?: any) {
    if (bool) {
      this.formValidate = bool === 'yes' ? true : false;
    } else {
      return this.formValidate;
    }
  }
}
